import React, { useState } from 'react';
import { Image as ImageIcon, ImageDown } from 'lucide-react';
import FileUpload from '../components/common/FileUpload';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import TaskProgressPanel from '../components/conversion/TaskProgressPanel';
import ConversionHistory from '../components/conversion/ConversionHistory';
import { useConversionProcess } from '../hooks/useConversionProcess';

type ImageFormat = 'png' | 'jpeg' | 'jpg' | 'gif';
type ColorType = 'color' | 'greyscale' | 'blackwhite';

const PDFToImage: React.FC = () => {
    const [files, setFiles] = useState<File[]>([]);
    const [imageFormat, setImageFormat] = useState<ImageFormat>('png');
    const [refreshKey, setRefreshKey] = useState(0);
    const [colorType, setColorType] = useState<ColorType>('color');
    const [dpi, setDpi] = useState<number>(300);

    const {
        taskId,
        isCreating,
        processError,
        setProcessError,
        executeTask,
        resetTask,
    } = useConversionProcess();

    const handleFilesSelected = (selectedFiles: File[]) => {
        setFiles(selectedFiles);
        setProcessError(null);
    };

    const handleConvert = async () => {
        if (files.length === 0) {
            setProcessError('请选择一个 PDF 文件');
            return;
        }

        await executeTask(files[0], 'pdf_to_image', {
            imageFormat,
            singleOrMultiple: 'multiple',
            colorType,
            dpi,
        });
    };

    const handleReset = () => {
        resetTask();
        setFiles([]);
    };

    const formatOptions = [
        { value: 'png' as const, label: 'PNG', description: '无损压缩，质量最高' },
        { value: 'jpeg' as const, label: 'JPEG', description: '有损压缩，文件较小' },
        { value: 'gif' as const, label: 'GIF', description: '支持动画，颜色有限' },
    ];

    const colorOptions = [
        { value: 'color' as const, label: '彩色', description: '保留原始颜色' },
        { value: 'greyscale' as const, label: '灰度', description: '转为灰度图像' },
        { value: 'blackwhite' as const, label: '黑白', description: '纯黑白图像' },
    ];

    const dpiOptions = [
        { value: 150, label: '150 DPI', description: '低质量，文件小' },
        { value: 300, label: '300 DPI', description: '标准质量（推荐）' },
        { value: 600, label: '600 DPI', description: '高质量，文件大' },
    ];

    return (
        <div className="pdf-to-image-page">
            <div className="container">
                <div className="page-header">
                    <h1>PDF 转图片</h1>
                    <p>将 PDF 页面转换为图片格式</p>
                </div>

                <div className="page-content">
                    <Card glass>
                        {!taskId ? (
                            <>
                                <div className="upload-section">
                                    <FileUpload
                                        onFilesSelected={handleFilesSelected}
                                        multiple={false}
                                        maxFiles={1}
                                    />
                                </div>

                                {files.length > 0 && (
                                    <>
                                        <div className="form-group">
                                            <label className="form-label">
                                                <ImageIcon size={20} style={{ display: 'inline', marginRight: '8px' }} />
                                                图片格式
                                            </label>
                                            <div className="compression-options">
                                                {formatOptions.map((format) => (
                                                    <label
                                                        key={format.value}
                                                        className={`compression-option ${imageFormat === format.value ? 'selected' : ''}`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="format"
                                                            value={format.value}
                                                            checked={imageFormat === format.value}
                                                            onChange={() => setImageFormat(format.value)}
                                                            disabled={isCreating}
                                                        />
                                                        <div className="option-content">
                                                            <strong>{format.label}</strong>
                                                            <span>{format.description}</span>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">颜色类型</label>
                                            <div className="compression-options">
                                                {colorOptions.map((color) => (
                                                    <label
                                                        key={color.value}
                                                        className={`compression-option ${colorType === color.value ? 'selected' : ''}`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="color"
                                                            value={color.value}
                                                            checked={colorType === color.value}
                                                            onChange={() => setColorType(color.value)}
                                                            disabled={isCreating}
                                                        />
                                                        <div className="option-content">
                                                            <strong>{color.label}</strong>
                                                            <span>{color.description}</span>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">图片质量 (DPI)</label>
                                            <div className="compression-options">
                                                {dpiOptions.map((option) => (
                                                    <label
                                                        key={option.value}
                                                        className={`compression-option ${dpi === option.value ? 'selected' : ''}`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="dpi"
                                                            value={option.value}
                                                            checked={dpi === option.value}
                                                            onChange={() => setDpi(option.value)}
                                                            disabled={isCreating}
                                                        />
                                                        <div className="option-content">
                                                            <strong>{option.label}</strong>
                                                            <span>{option.description}</span>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="action-section">
                                            <div className="file-count">
                                                已选择文件: <strong>{files[0].name}</strong>
                                            </div>

                                            <Button
                                                variant="primary"
                                                size="lg"
                                                onClick={handleConvert}
                                                isLoading={isCreating}
                                                icon={<ImageDown />}
                                                disabled={isCreating}
                                            >
                                                {isCreating ? '正在准备转换...' : '开始转换'}
                                            </Button>
                                        </div>
                                    </>
                                )}

                                {processError && (
                                    <div className="message message-error" style={{ marginTop: '15px' }}>
                                        {processError}
                                    </div>
                                )}
                            </>
                        ) : (
                            <TaskProgressPanel taskId={taskId} onReset={handleReset} onComplete={() => setRefreshKey(prev => prev + 1)} />
                        )}
                    </Card>

                    <div className="tips-section">
                        <Card>
                            <h3>💡 使用提示</h3>
                            <ul>
                                <li>PDF 的每一页都会转换为单独的图片文件</li>
                                <li>转换完成后，所有图片会被打包为一个 ZIP 压缩包供您下载</li>
                                <li>PNG 格式质量最高但文件较大，JPEG 格式文件较小</li>
                                <li>DPI 越高图片质量越好，但文件也越大</li>
                                <li>建议使用 300 DPI 以获得最佳质量和文件大小平衡</li>
                                <li>转换处理大文件可能需要一段时间，请耐心等待</li>
                            </ul>
                        </Card>
                    </div>

                    <ConversionHistory operationFilter="pdf_to_image" onRefreshTrigger={refreshKey} />
                </div>
            </div>
        </div>
    );
};

export default PDFToImage;
